import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

const Sidebar = ({ onCollapseChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  const navigationItems = [
    {
      label: 'Dashboard',
      path: '/dashboard',
      icon: 'LayoutDashboard',
      description: 'Overview and analytics'
    },
    {
      label: 'Production Workflow',
      path: '/production-workflow',
      icon: 'Factory',
      description: 'Manage production processes'
    },
    {
      label: 'Inventory Management',
      path: '/inventory-management',
      icon: 'Package',
      description: 'Stock and warehouse control'
    },
    {
      label: 'Invoice & Challan',
      path: '/invoice-challan-management',
      icon: 'FileText',
      description: 'Billing and documentation'
    },
    {
      label: 'Master Data',
      path: '/master-data-management',
      icon: 'Database',
      description: 'Configure system data'
    }
  ];

  const handleNavigation = (path) => {
    window.location.href = path;
    setIsMobileOpen(false);
  };

  const toggleCollapse = () => {
    const newValue = !isCollapsed;
    setIsCollapsed(newValue);
    localStorage.setItem('sidebarCollapsed', newValue);
    onCollapseChange(newValue);
  };

  const toggleMobile = () => {
    setIsMobileOpen(!isMobileOpen);

  };

  // Close mobile sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileOpen && !event.target.closest('.sidebar-container')) {
        setIsMobileOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileOpen]);

  // Prevent body scroll when mobile sidebar is open
  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.touchAction = 'none';
    } else {
      document.body.style.overflow = 'auto';
      document.body.style.touchAction = 'auto';
    }
  }, [isMobileOpen]);

  // Close mobile menu when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsMobileOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      {/* Mobile Overlay */}
      {/* {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          onClick={toggleMobile}
        />
      )} */}

      {/* Sidebar */}
      <aside className={`
  sidebar-container
  fixed top-16 left-0 h-[calc(100vh-4rem)]
  bg-card border-r border-border z-40
  transition-all duration-300 ease-in-out
  ${isCollapsed ? 'w-16' : 'w-64'}
  hidden lg:block
`}>

  <div className="flex flex-col h-full">
          {/* Sidebar Header */}
          <div className="flex items-center justify-between p-3 border-b border-border">
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <Icon name="Layers" size={20} className="text-primary" />
                <span className="font-semibold text-foreground">Navigation</span>
              </div>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleCollapse}
              iconName={isCollapsed ? "ChevronRight" : "ChevronLeft"}
              iconSize={16}
              className="hidden lg:flex hover:bg-accent/50"
              aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleMobile}
              iconName="X"
              iconSize={16}
              className="lg:hidden hover:bg-accent/50"
              aria-label="Close sidebar"
            />
          </div>

          {/* Navigation Items */}
          <nav className={`flex-1 p-2 space-y-1 ${isCollapsed ? 'overflow-hidden' : 'overflow-y-auto'}`}>
            {navigationItems.map((item) => {
              const isActive = location.pathname === item.path;

              return (
                <div key={item.path} className="relative group">
                  <Button
                    variant={isActive ? "default" : "ghost"}
                    size="sm"
                    onClick={() => handleNavigation(item.path)}
                    className={`
                      w-full justify-start transition-all duration-200
                      ${isCollapsed ? 'px-2 py-3' : 'px-3 py-3'}
                      ${isActive ? 'shadow-sm' : 'hover:bg-accent/50'}
                    `}
                    aria-label={item.label}
                    aria-current={isActive ? "page" : undefined}
                  >
                    <Icon
                      name={item.icon}
                      size={18}
                      className={`${isCollapsed ? 'mx-auto' : 'mr-3'} flex-shrink-0`}
                    />
                    {!isCollapsed && (
                      <div className="flex-1 text-left overflow-hidden">
                        <div className="font-medium truncate">{item.label}</div>
                        <div className={`text-xs mt-0.5 truncate ${isActive ? 'text-primary-foreground' : 'text-muted-foreground'}`}>
                          {item.description}
                        </div>
                      </div>
                    )}
                  </Button>

                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="
                      absolute left-full top-0 ml-2 px-3 py-2 bg-popover border border-border rounded-md shadow-lg
                      opacity-0 invisible group-hover:opacity-100 group-hover:visible
                      transition-all duration-200 z-50 whitespace-nowrap text-sm
                    ">
                      <div className="font-medium">{item.label}</div>
                      <div className="text-muted-foreground text-xs mt-1">{item.description}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* Sidebar Footer */}
          <div className="p-2 border-t border-border">
            {!isCollapsed ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-md">
                  <div className="w-6 h-6 bg-success rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="Check" size={12} color="white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">System Status</p>
                    <p className="text-xs text-success truncate">All systems operational</p>
                  </div>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  iconName="Settings"
                  iconPosition="left"
                  iconSize={16}
                  className="text-sm"
                >
                  Settings
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center space-y-2">
                <Button
                  variant="ghost"
                  size="icon"
                  iconName="Settings"
                  iconSize={18}
                  className="relative group hover:bg-accent/50"
                  aria-label="Settings"
                >
                  <div className="
                    absolute left-full top-0 ml-2 px-3 py-2 bg-popover border border-border rounded-md shadow-lg
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible
                    transition-all duration-200 z-50 whitespace-nowrap
                  ">
                    Settings
                  </div>
                </Button>

                <div className="w-6 h-6 bg-success/20 rounded-full flex items-center justify-center relative group">
                  <Icon name="Check" size={12} color="var(--color-success)" />
                  <div className="
                    absolute left-full top-0 ml-2 px-3 py-2 bg-popover border border-border rounded-md shadow-lg
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible
                    transition-all duration-200 z-50 whitespace-nowrap text-sm
                  ">
                    <div className="font-medium">System Status</div>
                    <div className="text-xs text-success mt-1">All systems operational</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Toggle Button */}
      {/* <Button
        variant="default"
        size="icon"
        onClick={toggleMobile}
        iconName={isMobileOpen ? "X" : "Menu"}
        iconSize={20}
        className={`
          fixed top-20 left-4 z-50 lg:hidden shadow-md
          transition-all duration-300
          ${isMobileOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}
        `}
        aria-label="Toggle sidebar"
      /> */}
    </>
  );
};

export default Sidebar;